import { useEffect, useMemo, useState } from 'react';
import BoutiqueClubAccueil from './BoutiqueClubAccueil';
import BoutiqueClubApercu from './BoutiqueClubApercu';
import BoutiqueClubPaiementRetour from './BoutiqueClubPaiementRetour';
import { FOND_ACCUEIL_BOUTIQUECLUB, cheminFondStockageGroupe, signerFondStockageGroupe } from './boutiqueClubFonds';
import { ROUTAGE_BOUTIQUECLUB_INITIAL, resoudreRouteBoutiqueClub, urlBoutiqueClub } from './boutiqueClubRouting';
import { retrouverPanierPayplugTest } from './boutiqueClubPayplugTest';
import './BoutiqueClubApercu.css';
import './BoutiqueClubPublic.css';

const slugPropre = (valeur) => String(valeur || '').trim().toLowerCase();
export const DELAI_PORTAIL_PUBLIC_MS = 12000;

const erreurDelai = () => {
  const erreur = new Error('Le portail public ne répond pas dans le délai attendu.');
  erreur.code = 'BOUTIQUECLUB_TIMEOUT';
  return erreur;
};

export const invoquerPortailPublic = async (clientSupabase, body, delaiMs = DELAI_PORTAIL_PUBLIC_MS) => {
  let minuterie;
  try {
    return await Promise.race([
      clientSupabase.functions.invoke('boutiqueclub-portail', { body }),
      new Promise((_, rejeter) => { minuterie = setTimeout(() => rejeter(erreurDelai()), delaiMs); }),
    ]);
  } finally { clearTimeout(minuterie); }
};

const messageIndisponible = (erreur) => erreur?.code === 'BOUTIQUECLUB_TIMEOUT'
  ? 'La liste des boutiques est momentanément indisponible. Réessayez plus tard.'
  : 'Impossible de charger les boutiques pour le moment.';

export const lieuRetraitPublic = (boutique) => {
  const point = boutique?.reception?.magasin || boutique?.point_retrait;
  if (boutique?.campagne?.lieu_retrait_libre) return boutique.campagne.lieu_retrait_libre;
  if (point?.source === 'boutiqueclub_points_retrait') {
    const localite = [point?.ville, point?.adresse].filter((valeur,index,liste)=>valeur&&liste.indexOf(valeur)===index).join(', ');
    return [point?.nom, localite].filter(Boolean).join(' — ');
  }
  return [point?.nom, point?.adresse, point?.code_postal, point?.ville].filter(Boolean).join(' — ');
};

export const personnalisationPublique = (boutique = {}) => {
  const personnalisation = boutique.personnalisation || {
    groupes_tarifaires:boutique.personnalisation_groupes_tarifaires || [],
    options:boutique.personnalisation_options || [],
    marquages:boutique.personnalisation_marquages || [],
    polices:boutique.personnalisation_polices || [],
  };
  return {
    ...personnalisation,
    visuels:personnalisation.visuels || boutique.medias?.visuels || boutique.personnalisation_visuels || [],
  };
};

const magasinsDuGroupe = (slug) => ROUTAGE_BOUTIQUECLUB_INITIAL.groupes.find((g) => g.slug === slug)?.magasins?.filter((m) => m.actif !== false) || [];
export const signerFondsPublics = async (clientSupabase, valeur) => {
  const campagnes = valeur?.campagnes || valeur?.clubs || [];
  const references = [...new Set(campagnes.map((item)=>item.couverture_path).filter(cheminFondStockageGroupe))];
  const signes = await Promise.all(references.map(async (reference)=>[reference,await signerFondStockageGroupe(clientSupabase,reference)]));
  return Object.fromEntries(signes.filter(([,url])=>url));
};

export default function BoutiqueClubPublic({ clientSupabase, groupeSlug, campagneInitiale, packInitial, documentInitial, routeInitiale }) {
  const routeDepart = routeInitiale || (groupeSlug
    ? { type:'GROUPE', groupeSlug:slugPropre(groupeSlug), magasinSlug:null }
    : { type:'PORTAIL' });
  const [route, setRoute] = useState(routeDepart);
  const [accueil, setAccueil] = useState(null);
  const [boutique, setBoutique] = useState(null);
  const [panier, setPanier] = useState(() => retrouverPanierPayplugTest(
    window.sessionStorage, routeDepart.campagneId, routeDepart.groupeSlug
  )?.panier || []);
  const [slugActif, setSlugActif] = useState(slugPropre(routeDepart.groupeSlug || groupeSlug));
  const [urlsPhotos, setUrlsPhotos] = useState({});
  const [urlsPhotosOriginales, setUrlsPhotosOriginales] = useState({});
  const [urlsPhotosVariantes, setUrlsPhotosVariantes] = useState({});
  const [message, setMessage] = useState('Chargement…');
  const magasins = useMemo(() => magasinsDuGroupe(route.groupeSlug), [route.groupeSlug]);

  const naviguer = (destination) => {
    const suivante = resoudreRouteBoutiqueClub(destination);
    window.history.pushState({}, '', destination);
    setBoutique(null); setRoute(suivante); setSlugActif(slugPropre(suivante.groupeSlug));
  };

  useEffect(() => {
    const retour = () => {
      const ancienGroupe=new URLSearchParams(window.location.search).get('boutiqueclub');
      const suivante=ancienGroupe && window.location.pathname==='/'
        ? {type:'GROUPE',groupeSlug:slugPropre(ancienGroupe),magasinSlug:null}
        : resoudreRouteBoutiqueClub(window.location.pathname);
      setBoutique(null); setRoute(suivante); setSlugActif(slugPropre(suivante.groupeSlug));
    };
    window.addEventListener('popstate', retour);
    return () => window.removeEventListener('popstate', retour);
  }, []);

  useEffect(() => {
    let actif = true;
    async function charger() {
      setAccueil(null); setMessage('Chargement…');
      if (route.type === 'PAIEMENT_RETOUR' || route.type === 'PAIEMENT_ANNULE') { setMessage(''); return; }
      if (route.type === 'INTROUVABLE' || route.type === 'ERREUR_CONFIGURATION') { setMessage('Page introuvable ou indisponible.'); return; }
      try {
        if (route.type === 'CAMPAGNE' || route.type === 'PACK') {
          await ouvrirCampagne({ id:route.campagneId, groupe_slug:route.groupeSlug, magasin_slug:route.magasinSlug }, false, route);
          return;
        }
        if (route.type === 'CLUB') {
          const { data, error } = await invoquerPortailPublic(clientSupabase, { action:'CLUB_PUBLIC', groupe_slug:route.groupeSlug, magasin_slug:route.magasinSlug || null, club_id:route.clubId });
          if (error || !data?.campagne_id) throw error || new Error('Club indisponible.');
          if (!actif) return;
          await ouvrirCampagne({ id:data.campagne_id, groupe_slug:route.groupeSlug }, false, route);
          return;
        }
        const estPortail = route.type === 'PORTAIL';
        const action = route.type === 'MAGASIN' ? 'ACCUEIL_MAGASIN_PUBLIC' : estPortail ? 'PORTAIL_PUBLIC' : 'ACCUEIL_PUBLIC';
        const body = estPortail ? { action } : { action, groupe_slug:route.groupeSlug, ...(route.magasinSlug ? { magasin_slug:route.magasinSlug } : {}) };
        const { data, error } = await invoquerPortailPublic(clientSupabase, body);
        if (!actif) return;
        const resultat = estPortail ? data?.portail : data?.accueil;
        if (error || !resultat) throw error || new Error('Réponse publique absente.');
        const accueilPublic = estPortail ? {
          ...resultat,
          campagnes:(resultat.clubs || []).map((club)=>({id:club.campagne_id,club_id:club.id,groupe_slug:club.groupe_slug,magasin_slug:club.magasin_slug,nom:club.nom_boutique,presentation_publique:club.presentation_publique===true,ouverture_at:club.ouverture_at,fermeture_at:club.fermeture_at,statut:club.commandes_ouvertes?'OUVERTE':'FERMEE'}))
        } : resultat;
        setAccueil(accueilPublic); setUrlsPhotos({...data?.urls_photos,...await signerFondsPublics(clientSupabase,accueilPublic)});
        setUrlsPhotosOriginales((courant)=>({...courant,...(data?.urls_photos_originales||{})}));
        setMessage((accueilPublic.clubs || []).length > 0 ? '' : 'Aucune boutique disponible pour le moment.');
      } catch (erreur) {
        if (!actif) return;
        setAccueil(null); setMessage(messageIndisponible(erreur));
      }
    }
    charger();
    return () => { actif = false; };
    // ouvrirCampagne repose volontairement sur le contexte de route courant.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientSupabase, route.type, route.groupeSlug, route.magasinSlug, route.clubId]);

  useEffect(() => {
    if (accueil && campagneInitiale && route.type !== 'CAMPAGNE' && route.type !== 'PACK') ouvrirCampagne({ id: campagneInitiale }, false, route);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accueil, campagneInitiale]);

  async function ouvrirCampagne(campagne, actualiserUrl = true, contexte = route) {
    setMessage('Chargement de la campagne…');
    const groupeCampagne=slugPropre(campagne.groupe_slug || contexte.groupeSlug || slugActif || groupeSlug);
    const magasinCampagne=slugPropre(campagne.magasin_slug || contexte.magasinSlug);
    try {
      const { data, error } = await invoquerPortailPublic(clientSupabase, {
        action:'CAMPAGNE_PUBLIQUE', groupe_slug:groupeCampagne, campagne_id:campagne.id,
        ...(magasinCampagne ? { magasin_slug:magasinCampagne } : {}),
      });
      if (error || !data?.boutique) throw error || new Error('Campagne absente.');
      setBoutique(data.boutique); setSlugActif(groupeCampagne);
      const referenceFond=data.boutique?.campagne?.couverture_path;
      const fondSigne=cheminFondStockageGroupe(referenceFond) ? await signerFondStockageGroupe(clientSupabase,referenceFond) : '';
      setUrlsPhotos((courant)=>({...courant,...(data.urls_photos||{}),...(fondSigne?{[referenceFond]:fondSigne}:{})})); setMessage('');
      setUrlsPhotosOriginales((courant)=>({...courant,...(data.urls_photos_originales||{})}));
      setUrlsPhotosVariantes((courant)=>({...courant,...(data.urls_photos_variantes||{})}));
      if (actualiserUrl) {
        const destination=urlBoutiqueClub({groupeSlug:groupeCampagne,magasinSlug:magasinCampagne,campagneId:campagne.id,packId:packInitial});
        window.history.pushState({},'',destination);
      }
    } catch (erreur) {
      setBoutique(null);
      setMessage(erreur?.code === 'BOUTIQUECLUB_TIMEOUT' ? 'La boutique est momentanément indisponible. Réessayez plus tard.' : 'Campagne introuvable.');
    }
  }

  function fermerCampagne() {
    setBoutique(null);
    const destination=urlBoutiqueClub({groupeSlug:route.groupeSlug || slugActif,magasinSlug:route.magasinSlug});
    window.history.pushState({},'',destination);
  }

  if (route.type === 'PAIEMENT_RETOUR' || route.type === 'PAIEMENT_ANNULE') return <BoutiqueClubPaiementRetour
    clientSupabase={clientSupabase} annulation={route.type === 'PAIEMENT_ANNULE'}/>;

  if (boutique) return <BoutiqueClubApercu
    campagne={boutique.campagne} publication={boutique.publication} club={boutique.club} groupe={slugActif}
    familles={boutique.familles || []} produits={boutique.produits || []} tailles={boutique.tailles || []} pictogrammes={boutique.pictogrammes || []}
    composants={boutique.composants || []} urlsPhotos={urlsPhotos} urlsPhotosOriginales={urlsPhotosOriginales} urlsPhotosVariantes={urlsPhotosVariantes} lieuRetrait={lieuRetraitPublic(boutique)} pointRetrait={boutique?.reception?.magasin || boutique?.point_retrait || null}
    parametres={boutique.parametres || {}} personnalisation={personnalisationPublique(boutique)} clientSupabase={clientSupabase} produitInitial={packInitial} documentInitial={documentInitial} panierGlobal={panier} setPanierGlobal={setPanier} onFermer={fermerCampagne}/>;

  const titre = route.type === 'PORTAIL' ? 'BOUTIQUECLUB' : route.magasin?.nom_public || route.groupe?.nom_public || String(route.groupeSlug || '').toUpperCase();
  return <main className="bc-public-home">
    <header className="bc-public-top"><button className="bc-brand" onClick={()=>naviguer('/')}>BOUTIQUECLUB</button><span>Paiement sécurisé · Retrait partenaire · Clubs officiels</span></header>
    <section className="bc-public-hero" style={{ '--bc-home-background': `url("${FOND_ACCUEIL_BOUTIQUECLUB}")` }}><div><p className="bc-context">{titre}</p><h1>CLUBS PARTENAIRES</h1><p>Trouvez la boutique officielle de votre club et équipez-vous aux couleurs de votre passion.</p></div></section>
    <section className="bc-public-content">
      {route.type !== 'PORTAIL' && <button className="bc-back" onClick={()=>naviguer(route.magasinSlug ? `/${route.groupeSlug}` : '/')}>← Retour</button>}
      {message && <p className={message.includes('introuvable') || message.includes('Impossible') ? 'bc-public-error' : ''} role="status">{message}</p>}
      {route.type === 'PORTAIL' && accueil && <section><h2>GROUPES DISPONIBLES</h2><div className="bc-route-grid">{(accueil.groupes || []).map((groupe)=><button key={groupe.slug} onClick={()=>naviguer(`/${groupe.slug}`)}><strong>{groupe.nom}</strong><span>Découvrir les magasins et Clubs</span></button>)}</div></section>}
      {route.type === 'GROUPE' && <section><h2>NOS MAGASINS</h2><div className="bc-route-grid">{magasins.map((magasin)=><button key={magasin.slug} onClick={()=>naviguer(`/${magasin.slug}`)}><strong>{magasin.nom_public}</strong><span>Voir les Clubs BoutiqueClub</span></button>)}</div></section>}
      {accueil && <BoutiqueClubAccueil clubs={accueil.clubs || []} campagnes={accueil.campagnes || []} urlsPhotos={urlsPhotos} afficherMagasin={route.type === 'GROUPE' || route.type === 'PORTAIL'} onOuvrir={ouvrirCampagne}/>}
    </section>
  </main>;
}

